import { jsPDF } from "jspdf";

export const formatPhoneNumber = (inputValue: any): any => {
  // Remove non-numeric characters from the input
  const cleanedInput = inputValue?.replace(/\D/g, '');
  // Check if the cleaned input matches the pattern of +1xxxxxxxxxx
  const match = cleanedInput?.match(/^1?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    // If there's a match, format the phone number as +1 (xxx) xxx-xxxx
    return `(${match[1]})-${match[2]}-${match[3]}`;
  }
  // If there's no match, return the cleaned input (possibly null if input is invalid)
  return cleanedInput;
};

export const formatCurrency=(amount: any): any => {
  // Convert amount to a number if it's a string
  const parsedAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  // Check if parsedAmount is a valid number
  if (!isNaN(parsedAmount)) {
    // Use toFixed only if parsedAmount is a valid number
    const formattedAmount = parsedAmount.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
    return `${formattedAmount}`;
  }

  // Return original amount if it's not a valid number
  return `${amount}`;
}

export function convertTimestampToDateFormat(timestamp:any) {
  // Create a new Date object using the timestamp
  const date = new Date(timestamp);

  // Get the month, day, and year from the Date object
  const month = date.getMonth() + 1; // Add 1 because getMonth() returns zero-based index
  const day = date.getDate();
  const year = date.getFullYear();

  // Format the date as mm/dd/yyyy
  const formattedDate = `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;

  return formattedDate;
}

export const formatDateString = (isoString:any) => {
  if (!isoString) {
    return ''; // Return empty string if input is empty or null/undefined
  }
  const date = new Date(isoString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // getMonth() is zero-based
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
export const dateString = (isoString:any) => {
  if (!isoString) {
    return ''; // Return empty string if input is empty or null/undefined
  }
  const date = new Date(isoString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // getMonth() is zero-based
  const day = String(date.getDate()).padStart(2, '0');
  return `${month}/${day}/${year}`;
};

export const formatDate = (isoString: any) => {
  const date = new Date(isoString);

  // Correcting the type issue here
  const optionsDate: Intl.DateTimeFormatOptions = {
    year: 'numeric',  // 'numeric' is valid
    month: '2-digit',  // '2-digit' is valid
    day: '2-digit'     // '2-digit' is valid
  };

  const optionsTime: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  };

  const formattedDate = date.toLocaleString('en-US', optionsDate);
  const formattedTime = date.toLocaleString('en-US', optionsTime);

  return `${formattedDate} ${formattedTime}`;
};


export function formatUploadDate() {
  const date = new Date();

  // Get parts of the date
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();

  // Get parts of the time
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours || 12; // the hour '0' should be '12'
  const formattedHours = String(hours).padStart(2, "0");

  // Combine date and time
  const formattedDate = `${month}/${day}/${year}`;
  const formattedTime = `${formattedHours}:${minutes}:${seconds} ${ampm}`;

  return `${formattedDate}, ${formattedTime}`;
}

// Upload Documents
export const forbiddenCharactersRegex = /^(?!.*\.\.)[^_~#%&*+,:;?/+|@!()"'{}=\[\]\\][^_~#%&*+,:;?/+|@!()"'{}=\[\]\\]*[^_~#%&*+,:;?/+|@!()"'{}=\[\]\\]$/

//preview extensions
export function getMimeType(extension:any) {
  switch (extension.toLowerCase()) {
    case 'txt':
      return 'text/plain';
    case 'pdf':
      return 'application/pdf';
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'xls':
      return 'application/vnd.ms-excel';
    case 'xlsx':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    case 'doc':
      return 'application/msword';
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    case 'htm':
      return 'text/htm';
    case 'html':
      return 'text/html';
    case 'csv':
      return 'text/csv';
    case 'ppt':
      return 'application/vnd.ms-powerpoint';
    case 'pptx':
      return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';

    case 'odt':
      return 'application/vnd.oasis.opendocument.text';
    case 'zip':
      return 'application/zip';
    case 'rar':
      return 'application/x-rar-compressed';
    // Add more cases as needed
    default:
      return 'application/octet-stream'; // Fallback MIME type
  }
}

import dayjs from "dayjs";

/**
 * Formats a given date string into "MMM DD, YYYY" format (e.g., Jan 26, 2025).
 * @param dateString - The date string from the database (ISO format).
 * @returns Formatted date as a string.
 */
export const formatToReadableDate = (dateString: string): string => {
  return dayjs(dateString).format("MMM DD, YYYY");
};

export function downloadAsPDF(text: string, filename: string) {
  const doc = new jsPDF();
  const lines = doc.splitTextToSize(text, 180);
  doc.text(lines, 10, 10);
  doc.save(filename);
}

export function downloadAsFile(text: string, filename: string, mimeType: string) {
  const blob = new Blob([text], { type: mimeType });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}